import { Box, IconButton, Menu, MenuItem, Stack, Typography, alpha, useTheme } from "@mui/material"
import { format } from "date-fns"
import { useState } from "react"
import Iconify from "minimals-template/components/Iconify"
import { UserAvatar } from "minimals-template/components/user-avatar"
import { If, Else } from "lib/@components/switch"

const AVATAR_COLUMN_WIDTH = 28

// One chat message in a thread, laid out like a chat history. Ownership is viewer-relative: the
// viewer's own messages sit right in a primary-tinted bubble and are captioned "You"; the other
// side sits left in neutral bubbles with the avatar anchored to the END of each cluster and the
// author captioned beneath it. Clustering is presentational only — every message keeps its own
// data-cy so per-message assertions are unaffected.
export function ShareLinkCommentMessage({ cluster, viewer, onEdit, onDelete }) {
    const theme = useTheme()
    const [menuAnchor, setMenuAnchor] = useState(null)
    const message = cluster?.message ?? cluster ?? {}
    const isClusterEnd = cluster?.isClusterEnd ?? true
    const isOwn = message.authorUserId === viewer?.userId
    const showMenu = isOwn && !message.deleted
    const displayName = message.authorName ?? "Unknown"

    return (
        <Stack
            spacing={0.25}
            data-cy={`share-link-comment-message-${message.id}`}
            sx={{ width: 1, alignItems: isOwn ? "flex-end" : "flex-start" }}
        >
            <Stack direction={getRowDirection()} spacing={1} alignItems="flex-end" sx={{ maxWidth: "82%" }}>
                <If value={!isOwn}>
                    <If value={isClusterEnd}>
                        <UserAvatar user={getAvatarUser()} sx={getAvatarSx()} />
                        <Else>
                            <Box sx={getAvatarSpacerSx()} />
                        </Else>
                    </If>
                </If>
                <If value={!!message.deleted}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 0.5 }}>
                        message removed
                    </Typography>
                    <Else>
                        <Stack direction={getRowDirection()} spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                            <Box sx={getBubbleSx()}>
                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {message.body}
                                </Typography>
                            </Box>
                            <If value={showMenu}>
                                <IconButton size="small" onClick={openMenu} aria-label="message actions">
                                    <Iconify icon="mdi:dots-horizontal" />
                                </IconButton>
                            </If>
                        </Stack>
                    </Else>
                </If>
            </Stack>
            <If value={isClusterEnd || !!message.editedAt}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={getCaptionSx()}>
                    <Typography variant="caption" color="text.secondary">
                        {getCaption()}
                    </Typography>
                    <If value={isOwn && isClusterEnd}>
                        <Iconify icon="mdi:check" width={12} sx={{ color: "text.disabled" }} />
                    </If>
                </Stack>
            </If>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
                <MenuItem onClick={handleEdit}>Edit</MenuItem>
                <MenuItem onClick={handleDelete}>Delete</MenuItem>
            </Menu>
        </Stack>
    )

    function getRowDirection() {
        return isOwn ? "row-reverse" : "row"
    }

    // Chat caption beneath the cluster: "You · 09:14" for own messages, "Name · 09:14" for the
    // other side, with "edited" appended per edited message.
    function getCaption() {
        const parts = []
        if (isClusterEnd) {
            parts.push(isOwn ? "You" : displayName)
            const time = getTime()
            if (time) parts.push(time)
        }
        if (message.editedAt) parts.push("edited")
        return parts.join(" · ")
    }

    function getCaptionSx() {
        return {
            pl: isOwn ? 0 : `${AVATAR_COLUMN_WIDTH + 8}px`,
            pr: isOwn ? 0.5 : 0,
        }
    }

    function getAvatarUser() {
        return { displayName }
    }

    function getTime() {
        const date = new Date(message.created)
        if (Number.isNaN(date.getTime())) return ""
        return format(date, "HH:mm")
    }

    function getAvatarSx() {
        return {
            width: AVATAR_COLUMN_WIDTH,
            height: AVATAR_COLUMN_WIDTH,
            flexShrink: 0,
            "& .MuiTypography-root": {
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1,
            },
        }
    }

    // Keeps clustered bubbles aligned with the avatar column without repeating the avatar.
    function getAvatarSpacerSx() {
        return { width: AVATAR_COLUMN_WIDTH, flexShrink: 0 }
    }

    // Ownership-keyed tint: the viewer's own messages read as primary, the other side stays neutral.
    // Chat-style corners: only the LAST bubble of a cluster gets a tightened "tail" corner on the
    // speaking side. NOTE: the individual corner properties are raw CSS in sx (no theme multiplier,
    // unlike borderRadius), so all four corners are set explicitly in px.
    function getBubbleSx() {
        const radius = "16px"
        const tail = "4px"
        return {
            px: 1.25,
            py: 0.625,
            borderTopLeftRadius: radius,
            borderTopRightRadius: radius,
            borderBottomRightRadius: isOwn && isClusterEnd ? tail : radius,
            borderBottomLeftRadius: !isOwn && isClusterEnd ? tail : radius,
            bgcolor: getBubbleColor(),
            maxWidth: 1,
        }
    }

    function getBubbleColor() {
        if (isOwn) return alpha(theme.palette.primary.main, 0.16)
        return theme.palette.background.neutral ?? alpha(theme.palette.grey[500], 0.12)
    }

    function openMenu(event) {
        setMenuAnchor(event.currentTarget)
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    function handleEdit() {
        closeMenu()
        onEdit?.(message)
    }

    function handleDelete() {
        closeMenu()
        onDelete?.(message)
    }
}
